import { useEffect, useRef, useState } from 'react';
import { ConsumableDefinitionView, ConsumableType } from '../types';

/**
 * The header's consumable actions, grouped behind one control (overhaul spec §2:
 * no emoji-as-toolbar — the emoji stay INSIDE the menu beside their labels, where
 * they read as content, not chrome).
 */
export function CareMenu({ definitions, onGive }: {
  definitions: ConsumableDefinitionView[];
  onGive: (type: ConsumableType) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  if (!definitions || definitions.length === 0) return null;

  return (
    <div className="care-menu" ref={ref}>
      <button
        className={`header-icon-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
        title="Care — give something"
        aria-label="Care menu"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="17" height="17">
          <path d="M19 14c1.5-1.5 3-3.2 3-5.3C22 6 20 4 17.6 4c-1.5 0-2.9.7-3.6 1.9C13.3 4.7 11.9 4 10.4 4 8 4 6 6 6 8.7c0 2.1 1.5 3.8 3 5.3l5 4.7 5-4.7Z" />
          <path d="M2 12h3l2 4 3-8 2 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="care-menu-panel" role="menu">
          <div className="care-menu-title">Give her something</div>
          <div className="care-menu-grid">
            {definitions.map(def => {
              const maxed = def.activeStacks >= def.maxStacks;
              return (
                <button
                  key={def.type}
                  role="menuitem"
                  className={`care-item ${maxed ? 'maxed' : ''}`}
                  disabled={maxed}
                  onClick={() => { onGive(def.type); setOpen(false); }}
                  title={maxed ? `${def.label} (at max)` : def.label}
                >
                  <span className="care-item-icon" aria-hidden>{def.icon}</span>
                  <span className="care-item-label">{def.label}</span>
                  {def.activeStacks > 0 && (
                    <span className="care-item-stacks">{def.activeStacks}/{def.maxStacks}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
